// /careers/ interactions. Each part enhances ordinary blocks (editors change the photos,
// values, offices and quotes in the block editor) and does nothing if its block is missing:
//   .careers-grow     a graph of roots grows from the photo's sprout as the section scrolls in
//   .careers-values   each value's Cypher (its code block) types into a sticky console
//   .careers-roles    open roles from Greenhouse, searched and filtered by department and region
//   .careers-cities   a photo of the hovered office follows the cursor
//   .careers-quotes   the quotes play as a reel, navigated by the people as a chain of nodes
// The hero graph is careers-graph.js. With reduced motion the roots are drawn grown, nothing types
// or plays, and every part stays readable.
(function () {
	var reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
	var canHover = window.matchMedia && window.matchMedia('(hover: hover)').matches;
	var esc = function (s) {
		var d = document.createElement('div');
		d.textContent = s == null ? '' : String(s);
		return d.innerHTML;
	};

	/* Roots */
	document.querySelectorAll('.careers-grow').forEach(function (section) {
		var img = section.querySelector('.careers-grow__photo img');
		var canvas = document.createElement('canvas');
		var ctx = canvas.getContext && canvas.getContext('2d');
		if (!img || !ctx || !window.requestAnimationFrame) { return; }

		var root = window.getComputedStyle(document.documentElement);
		var token = function (slug, fallback) {
			var value = root.getPropertyValue('--wp--preset--color--' + slug).trim();
			var hex = (/^#[0-9a-f]{6}$/i.test(value) ? value : fallback).slice(1);
			return [parseInt(hex.slice(0, 2), 16), parseInt(hex.slice(2, 4), 16), parseInt(hex.slice(4, 6), 16)];
		};
		var rgba = function (c, a) { return 'rgba(' + c[0] + ',' + c[1] + ',' + c[2] + ',' + Math.max(0, Math.min(1, a)).toFixed(3) + ')'; };
		var mix = function (a, b, t) { return [Math.round(a[0] + (b[0] - a[0]) * t), Math.round(a[1] + (b[1] - a[1]) * t), Math.round(a[2] + (b[2] - a[2]) * t)]; };
		var clamp = function (v, lo, hi) { return v < lo ? lo : v > hi ? hi : v; };
		var STEM = token('forest-30', '#6fa646');
		var ROOT = token('baltic-45', '#30839d');
		var LINK = token('baltic-30', '#5db3bf');
		var TIP = token('marigold-35', '#ffa901');

		var nodes = [], edges = [], links = [], tips = [], pulses = [];
		var W = 0, H = 0, dpr = 1, stemWidth = 3, builtWidth = 0;
		var shown = 0, target = 0, raf = 0, onScreen = false, lastPulse = 0;

		canvas.className = 'careers-grow__canvas';
		canvas.setAttribute('aria-hidden', 'true');
		section.insertBefore(canvas, section.firstChild);
		section.classList.add('is-enhanced');

		// Seeded, so every visit and every resize grows the same roots.
		function prng(seed) {
			return function () {
				seed = seed + 0x6D2B79F5 | 0;
				var t = Math.imul(seed ^ seed >>> 15, 1 | seed);
				t = t + Math.imul(t ^ t >>> 7, 61 | t) ^ t;
				return ((t ^ t >>> 14) >>> 0) / 4294967296;
			};
		}

		function build() {
			var box = section.getBoundingClientRect(), photo = img.getBoundingClientRect();
			if (!photo.width) { return; }
			var rand = prng(11), narrow = window.innerWidth < 782, floor, maxT = 0;
			W = box.width;
			H = box.height;
			builtWidth = window.innerWidth;
			dpr = Math.min(window.devicePixelRatio || 1, 2);
			canvas.width = Math.round(W * dpr);
			canvas.height = Math.round(H * dpr);
			floor = H - 24;
			stemWidth = Math.max(2, photo.width / 900 * 6);

			// Where the sprout's stem meets the photo's bottom edge (the photo is 900 x 680).
			var base = { x: photo.left - box.left + photo.width * 0.7167, y: photo.bottom - box.top - 1, depth: 0, t: 0 };
			var stem = { x: base.x, y: base.y + (narrow ? 14 : 20), depth: 0, t: 0.06, parent: base };
			nodes = [base, stem];
			edges = [{ a: base, b: stem, t0: 0, t1: 0.06, depth: 0, bend: 0 }];
			stem.edge = edges[0];
			links = [];
			tips = [];

			function grow(from, x, y, depth, t0, runner) {
				var node = { x: x, y: y, depth: depth, t: t0 + (runner ? 0.05 : 0.09 + rand() * 0.04), kids: 0, parent: from, runner: runner };
				nodes.push(node);
				// Each edge bows a little to one side, like a root.
				edges.push({ a: from, b: node, depth: depth, runner: runner, bend: (rand() - 0.5) * (runner ? 0.3 : 0.45), k: runner ? (from.k || 0) + 1 : 0 });
				node.k = runner ? (from.k || 0) + 1 : 0;
				node.edge = edges[edges.length - 1];
				from.kids = (from.kids || 0) + 1;
				maxT = Math.max(maxT, node.t);
				return node;
			}

			function branch(from, angle, len, depth, limit) {
				var ang = clamp(angle + (rand() - 0.5) * 0.5, Math.PI * 0.15, Math.PI * 0.85);
				var x = from.x + Math.cos(ang) * len, y = from.y + Math.sin(ang) * len;
				if (x < 16 || x > W - 16 || y > floor) { return; }
				var node = grow(from, x, y, depth, from.t, false);
				if (depth >= limit) { return; }
				var n = depth < 3 ? (rand() < 0.6 ? 2 : 1) : rand() < 0.35 ? 2 : rand() < 0.6 ? 1 : 0;
				for (var i = 0; i < n; i++) {
					var turn = n === 2 ? (i ? 1 : -1) * (0.3 + rand() * 0.3) : (rand() - 0.5) * 0.4;
					branch(node, ang + turn, len * (0.74 + rand() * 0.14), depth + 1, limit);
				}
			}

			// Two runners spread sideways from the stem, just below the photo, and roots drop
			// from them; a tap root goes straight down.
			var seg = narrow ? 46 : 72, drop = narrow ? 40 : 48, deep = narrow ? 4 : 5;
			var lo = base.y + (narrow ? 16 : 24), hi = base.y + (narrow ? 46 : 80);
			[-1, 1].forEach(function (dir) {
				var prev = stem, slope = 0.5;
				for (;;) {
					slope = slope * 0.5 + (rand() - 0.45) * 0.6 + (prev.y > hi ? -0.4 : prev.y < lo ? 0.3 : 0);
					var x = prev.x + dir * seg * (0.85 + rand() * 0.3), y = prev.y + clamp(slope, -0.5, 0.6) * seg * 0.5;
					if (x < 24 || x > W - 24) { break; }
					prev = grow(prev, x, y, 1, prev.t, true);
					if (rand() < 0.78) { branch(prev, Math.PI * 0.5, drop * (0.8 + rand() * 0.4), 2, deep); }
				}
			});
			branch(stem, Math.PI * 0.5, drop * 1.2, 2, deep + 1);

			// The roots finish growing a little before the scroll does.
			nodes.forEach(function (n) { n.t = n.t / maxT * 0.9; });
			edges.forEach(function (e) {
				e.t0 = e.a.t;
				e.t1 = e.b.t;
			});
			nodes.forEach(function (n) { if (n.depth > 0 && !n.kids) { tips.push(n); } });

			// Relationships between neighboring roots turn the tree into a graph.
			var reach = narrow ? 48 : 76, most = narrow ? 8 : 18;
			for (var i = 2; i < nodes.length && links.length < most; i++) {
				for (var j = i + 1; j < nodes.length; j++) {
					var a = nodes[i], b = nodes[j];
					if (a.depth > 2 && b.depth > 2 && a.parent !== b && b.parent !== a && a.parent !== b.parent && Math.abs(a.depth - b.depth) <= 1 && Math.hypot(a.x - b.x, a.y - b.y) < reach && rand() < 0.5) {
						links.push({ a: a, b: b, t: 0.84 + rand() * 0.1 });
						break;
					}
				}
			}
			pulses = [];
		}

		// A pulse runs from the stem down one root to its tip.
		function addPulse() {
			var tip = tips[Math.floor(Math.random() * tips.length)], path = [];
			for (var n = tip; n; n = n.parent) { path.unshift(n); }
			pulses.push({ path: path, i: 0, t: 0 });
		}

		// A point along an edge's curve (a quadratic bowed by its bend).
		function along(e, u) {
			var mx = (e.a.x + e.b.x) / 2, my = (e.a.y + e.b.y) / 2, dx = e.b.x - e.a.x, dy = e.b.y - e.a.y;
			var cx = mx - dy * e.bend, cy = my + dx * e.bend, v = 1 - u;
			return { x: v * v * e.a.x + 2 * v * u * cx + u * u * e.b.x, y: v * v * e.a.y + 2 * v * u * cy + u * u * e.b.y };
		}

		function draw(now) {
			ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
			ctx.clearRect(0, 0, W, H);
			ctx.lineCap = 'round';
			edges.forEach(function (e) {
				var g = clamp((shown - e.t0) / Math.max(0.001, e.t1 - e.t0), 0, 1);
				if (!g) { return; }
				ctx.strokeStyle = e.depth ? rgba(mix(STEM, ROOT, Math.min(1, e.depth / 3)), 0.8) : rgba(STEM, 1);
				ctx.lineWidth = !e.depth ? stemWidth : e.runner ? Math.max(1.2, stemWidth * 0.6 * (1 - Math.min(e.k, 12) / 12 * 0.5)) : Math.max(0.9, stemWidth * 0.6 - e.depth * 0.22);
				ctx.beginPath();
				ctx.moveTo(e.a.x, e.a.y);
				for (var i = 1; i <= 8; i++) {
					var pt = along(e, g * i / 8);
					ctx.lineTo(pt.x, pt.y);
				}
				ctx.stroke();
			});
			ctx.setLineDash([2, 4]);
			ctx.lineWidth = 1;
			links.forEach(function (l) {
				var f = clamp((shown - l.t) / 0.06, 0, 1);
				if (!f) { return; }
				ctx.strokeStyle = rgba(LINK, 0.6 * f);
				ctx.beginPath();
				ctx.moveTo(l.a.x, l.a.y);
				ctx.lineTo(l.b.x, l.b.y);
				ctx.stroke();
			});
			ctx.setLineDash([]);
			nodes.forEach(function (n) {
				// Branch points and tips; a root that just bends needs no node.
				if (n.depth < 1 || shown < n.t || n.kids === 1) { return; }
				var p = clamp((shown - n.t) / 0.04, 0, 1), s = 1 + 2.70158 * Math.pow(p - 1, 3) + 1.70158 * Math.pow(p - 1, 2);
				if (!n.kids) {
					var breathe = reduced ? 1 : 1 + 0.15 * Math.sin(now * 0.002 + n.x);
					ctx.fillStyle = rgba(TIP, 0.18);
					ctx.beginPath();
					ctx.arc(n.x, n.y, 7 * s * breathe, 0, Math.PI * 2);
					ctx.fill();
					ctx.fillStyle = rgba(TIP, 1);
					ctx.beginPath();
					ctx.arc(n.x, n.y, 3 * s, 0, Math.PI * 2);
					ctx.fill();
					return;
				}
				ctx.fillStyle = '#fff';
				ctx.strokeStyle = rgba(mix(STEM, ROOT, Math.min(1, n.depth / 3)), 1);
				ctx.lineWidth = 1.5;
				ctx.beginPath();
				ctx.arc(n.x, n.y, Math.max(2.2, 4.6 - n.depth * 0.35) * s, 0, Math.PI * 2);
				ctx.fill();
				ctx.stroke();
			});
			pulses.forEach(function (p) {
				var b = p.path[p.i + 1];
				if (!b) { return; }
				var pt = b.edge ? along(b.edge, p.t) : b, x = pt.x, y = pt.y;
				ctx.fillStyle = rgba(TIP, 0.25);
				ctx.beginPath();
				ctx.arc(x, y, 6, 0, Math.PI * 2);
				ctx.fill();
				ctx.fillStyle = rgba(TIP, 1);
				ctx.beginPath();
				ctx.arc(x, y, 2.4, 0, Math.PI * 2);
				ctx.fill();
			});
		}

		function measure() {
			var base = nodes[0];
			if (!base) { return; }
			var y = section.getBoundingClientRect().top + base.y, vh = window.innerHeight;
			target = reduced ? 1 : clamp((vh * 0.95 - y) / (vh * 0.6), 0, 1);
		}

		function frame(now) {
			raf = 0;
			shown = reduced ? target : shown + (target - shown) * 0.1;
			if (Math.abs(target - shown) < 0.0005) { shown = target; }
			if (!reduced && shown > 0.95 && tips.length) {
				if (now - lastPulse > 700 && pulses.length < 5) {
					addPulse();
					lastPulse = now;
				}
				pulses.forEach(function (p) {
					var a = p.path[p.i], b = p.path[p.i + 1];
					p.t += b ? 2.2 / Math.max(1, Math.hypot(b.x - a.x, b.y - a.y)) : 1;
					if (p.t >= 1) {
						p.i++;
						p.t = 0;
					}
				});
				pulses = pulses.filter(function (p) { return p.i < p.path.length - 1; });
			} else {
				pulses = [];
			}
			draw(now);
			if (onScreen && (shown !== target || (!reduced && shown > 0.95))) { raf = window.requestAnimationFrame(frame); }
		}

		function kick() {
			measure();
			if (!raf && onScreen) { raf = window.requestAnimationFrame(frame); }
		}

		function start() {
			build();
			kick();
		}
		if (img.complete) { start(); } else { img.addEventListener('load', start); }
		window.addEventListener('scroll', kick, { passive: true });
		if ('ResizeObserver' in window) {
			new ResizeObserver(function () {
				if (img.complete) { start(); }
			}).observe(section);
		} else {
			window.addEventListener('resize', function () { if (window.innerWidth !== builtWidth) { start(); } });
		}
		if ('IntersectionObserver' in window) {
			new IntersectionObserver(function (entries) {
				onScreen = entries[0].isIntersecting;
				kick();
			}).observe(section);
		} else {
			onScreen = true;
		}
	});

	/* Values console */
	document.querySelectorAll('.careers-values').forEach(function (section) {
		var items = Array.prototype.slice.call(section.querySelectorAll('.careers-values__item'));
		var list = section.querySelector('.careers-values__list');
		if (!items.length || !list) { return; }
		var consoleEl = document.createElement('div');
		consoleEl.className = 'careers-values__console';
		consoleEl.setAttribute('aria-hidden', 'true');
		consoleEl.innerHTML = '<div class="careers-values__bar"><i></i><i></i><i></i></div><div class="careers-values__prompt">neo4j$ MATCH</div><div class="careers-values__query"></div><div class="careers-values__result"></div>';
		list.parentNode.insertBefore(consoleEl, list);
		section.classList.add('is-enhanced');
		var query = consoleEl.querySelector('.careers-values__query');
		var result = consoleEl.querySelector('.careers-values__result');
		var timer = null;
		var current = -1;
		var colorize = function (text) {
			return esc(text)
				.replace(/(\([^)]*\))/g, '<span class="n">$1</span>')
				.replace(/(\[[^\]]*\])/g, '<span class="r">$1</span>');
		};
		function show(index) {
			var code = items[index].querySelector('code');
			var title = items[index].querySelector('h3');
			var text = code ? code.textContent : '';
			window.clearInterval(timer);
			result.textContent = '';
			var done = function () { result.textContent = '1 row · ' + (title ? title.textContent : ''); };
			if (reduced) { query.innerHTML = colorize(text); done(); return; }
			var n = 0;
			timer = window.setInterval(function () {
				n++;
				query.innerHTML = colorize(text.slice(0, n)) + '<span class="careers-values__caret"></span>';
				if (n >= text.length) { window.clearInterval(timer); done(); }
			}, 32);
		}
		function onScroll() {
			var index = 0;
			items.forEach(function (item, i) { if (item.getBoundingClientRect().top < window.innerHeight * 0.5) { index = i; } });
			items.forEach(function (item, i) { item.classList.toggle('is-active', i === index); });
			if (index !== current) { current = index; show(index); }
		}
		window.addEventListener('scroll', onScroll, { passive: true });
		onScroll();
	});

	/* Open roles */
	document.querySelectorAll('.careers-roles').forEach(function (root) {
		var board = root.getAttribute('data-board');
		var select = root.querySelector('.careers-roles__department');
		var place = root.querySelector('.careers-roles__location');
		var search = root.querySelector('.careers-roles__search');
		var count = root.querySelector('.careers-roles__plain');
		var list = root.querySelector('.careers-roles__list');
		var jobs = [];
		function render() {
			var department = select.value, region = place.value, typed = search ? search.value.trim() : '', term = typed.toLowerCase();
			var hits = jobs.filter(function (job) {
				return (!department || job.department === department) && (!region || job.regions.indexOf(region) !== -1) &&
					(!term || (job.title + ' ' + job.department + ' ' + job.location).toLowerCase().indexOf(term) !== -1);
			});
			count.textContent = plain(hits.length, department, region, typed);
			list.innerHTML = hits.map(function (job) {
				return '<li><a class="careers-roles__role" href="' + esc(job.url) + '"><b>' + esc(job.title) + '</b><span>' + esc(job.department) + '</span><span>' + esc(job.location) + '</span><i aria-hidden="true">→</i></a></li>';
			}).join('');
		}
		// The query read back as a sentence, for anyone who doesn't read Cypher.
		function plain(n, department, region, typed) {
			var kind = department ? department + ' ' : '';
			var where = region ? ' in ' + region : '';
			if (!department && !region && !typed) { return 'Showing all ' + n + ' open roles, in every department and region.'; }
			if (!n) { return 'No ' + kind + 'roles' + where + (typed ? ' mention \u201c' + typed + '\u201d' : '') + ' right now. Try a broader search.'; }
			return 'Showing ' + n + ' ' + kind + (n === 1 ? 'role' : 'roles') + where + (typed ? ' that ' + (n === 1 ? 'mentions' : 'mention') + ' \u201c' + typed + '\u201d' : '') + '.';
		}

		function options(target, names) {
			names.forEach(function (name) {
				var option = document.createElement('option');
				option.textContent = name;
				target.appendChild(option);
			});
		}
		// The offices list carries every job with its department; a job appears once per office.
		fetch('https://boards-api.greenhouse.io/v1/boards/' + encodeURIComponent(board) + '/offices')
			.then(function (response) { return response.json(); })
			.then(function (data) {
				var offices = data.offices || [], byId = {}, byJob = {}, departments = [], regions = [];
				offices.forEach(function (office) { byId[office.id] = office; });
				offices.forEach(function (office) {
					var top = office;
					while (top.parent_id && byId[top.parent_id]) { top = byId[top.parent_id]; }
					(office.departments || []).forEach(function (department) {
						(department.jobs || []).forEach(function (job) {
							var entry = byJob[job.id];
							if (!entry) {
								entry = byJob[job.id] = { title: job.title, department: department.name, location: (job.location && job.location.name) || '', url: job.absolute_url, regions: [] };
								jobs.push(entry);
							}
							// Greenhouse's "No Office" has id 0 and no region.
							if (top.id && entry.regions.indexOf(top.name) === -1) { entry.regions.push(top.name); }
							if (departments.indexOf(department.name) === -1) { departments.push(department.name); }
							if (top.id && regions.indexOf(top.name) === -1) { regions.push(top.name); }
						});
					});
				});
				var byName = function (a, b) { return a.localeCompare(b); };
				options(select, departments.sort(byName));
				options(place, regions.sort(byName));
				jobs.sort(function (a, b) { return a.title.localeCompare(b.title); });
				render();
			})
			.catch(function () { count.textContent = count.getAttribute('data-error'); });
		select.addEventListener('change', render);
		place.addEventListener('change', render);
		if (search) { search.addEventListener('input', render); }
	});

	/* Office photo follows the cursor */
	document.querySelectorAll('.careers-cities').forEach(function (section) {
		if (!canHover) { return; }
		var follow = document.createElement('div');
		var photo = document.createElement('img');
		follow.className = 'careers-cities__follow';
		follow.setAttribute('aria-hidden', 'true');
		photo.alt = '';
		follow.appendChild(photo);
		document.body.appendChild(follow);
		section.classList.add('is-enhanced');
		section.querySelectorAll('.careers-cities__row').forEach(function (row) {
			var img = row.querySelector('img');
			if (!img) { return; }
			row.addEventListener('mouseenter', function () { photo.src = img.currentSrc || img.src; follow.classList.add('is-visible'); });
			row.addEventListener('mouseleave', function () { follow.classList.remove('is-visible'); });
		});
		window.addEventListener('mousemove', function (e) { follow.style.left = e.clientX + 'px'; follow.style.top = e.clientY + 'px'; });
	});

	/* Life as a Node: the quotes play as a reel, navigated by a chain of the people as nodes */
	document.querySelectorAll('.careers-quotes').forEach(function (reel) {
		var quotes = Array.prototype.slice.call(reel.querySelectorAll('.careers-quotes__item'));
		if (quotes.length < 2) { return; }
		var nav = document.createElement('div');
		var chain = document.createElement('ol');
		var nodes = [];
		var index = 0, elapsed = 0, last = 0, held = false, looping = false, begun = false;
		var durations = [];
		var onScreen = !('IntersectionObserver' in window);
		nav.className = 'careers-quotes__nav';
		chain.className = 'careers-quotes__nodes';

		// Each word gets its place in line, so the words rise in one after another.
		function split(el) {
			var n = 0;
			Array.prototype.slice.call(el.childNodes).forEach(function (node) {
				if (node.nodeType !== 3) { return; }
				var frag = document.createDocumentFragment();
				node.textContent.split(/(\s+)/).forEach(function (part) {
					if (!part) { return; }
					if (/^\s+$/.test(part)) {
						frag.appendChild(document.createTextNode(part));
						return;
					}
					var word = document.createElement('span');
					word.className = 'careers-quotes__word';
					word.style.setProperty('--i', n++);
					word.textContent = part;
					frag.appendChild(word);
				});
				node.parentNode.replaceChild(frag, node);
			});
		}

		quotes.forEach(function (quote, i) {
			var text = quote.querySelector('.careers-quotes__quote');
			var img = quote.querySelector('img');
			var name = quote.querySelector('strong');
			var item = document.createElement('li');
			var button = document.createElement('button');
			var first = name ? name.textContent.trim().split(/\s+/)[0] : '';
			if (text && !reduced) { split(text); }
			// Long enough to read: about 280ms a word, never under 9 seconds.
			durations.push(Math.max(9000, (text ? text.textContent.trim().split(/\s+/).length : 0) * 280));
			button.type = 'button';
			button.className = 'careers-quotes__node';
			button.setAttribute('aria-label', 'Show quote ' + (i + 1) + ' of ' + quotes.length + (name ? ': ' + name.textContent.trim() : ''));
			button.innerHTML = '<span class="careers-quotes__avatar"><svg class="careers-quotes__ring" viewBox="0 0 74 74" aria-hidden="true"><circle cx="37" cy="37" r="35.5"/><circle cx="37" cy="37" r="35.5" pathLength="1"/></svg><span class="careers-quotes__photo">' + (img ? '<img alt="" src="' + esc(img.currentSrc || img.src) + '">' : '') + '</span></span><span class="careers-quotes__who" aria-hidden="true">' + esc(first) + '</span>';
			button.addEventListener('click', function () { show(i); });
			item.appendChild(button);
			if (i < quotes.length - 1) {
				var edge = document.createElement('i');
				edge.className = 'careers-quotes__edge';
				edge.setAttribute('aria-hidden', 'true');
				edge.innerHTML = '<b></b>';
				item.appendChild(edge);
			}
			nodes.push({ item: item, button: button });
			chain.appendChild(item);
		});

		// A reel that advances on its own needs a way to stop it (WCAG 2.2.2).
		var toggle = document.createElement('button');
		var stopped = reduced;
		toggle.type = 'button';
		toggle.className = 'careers-quotes__toggle';
		toggle.innerHTML = '<span class="careers-quotes__icon" aria-hidden="true"></span><span class="show-for-sr"></span>';
		var label = function () {
			toggle.lastChild.textContent = stopped ? 'Play' : 'Pause';
			toggle.setAttribute('aria-pressed', stopped ? 'true' : 'false');
			toggle.classList.toggle('is-stopped', stopped);
		};
		toggle.addEventListener('click', function () {
			stopped = !stopped;
			label();
			run();
		});
		label();
		nav.appendChild(chain);
		nav.appendChild(toggle);
		reel.appendChild(nav);
		reel.classList.add('is-enhanced');

		// Keyboard focus holds the reel; a click doesn't, or the ring would freeze on the quote
		// just picked. Pause/Play is the control for stopping it.
		reel.addEventListener('focusin', function (e) {
			held = e.target.matches && e.target.matches(':focus-visible');
		});
		reel.addEventListener('focusout', function () {
			held = false;
			run();
		});
		if ('IntersectionObserver' in window) {
			// The first quote comes in when the reel is first seen, not at page load.
			new IntersectionObserver(function (entries) {
				onScreen = entries[0].isIntersecting;
				if (onScreen && !begun) {
					begun = true;
					show(0);
				}
				run();
			}, { threshold: 0.35 }).observe(reel);
		}

		function show(i) {
			index = i;
			elapsed = 0;
			quotes.forEach(function (quote, k) { quote.classList.toggle('is-current', k === i); });
			nodes.forEach(function (node, k) {
				node.item.classList.toggle('is-past', k < i);
				node.button.setAttribute('aria-current', k === i ? 'true' : 'false');
				node.button.style.setProperty('--p', '0');
			});
		}

		// The current person's ring fills while the reel plays; it holds while stopped, off screen
		// or keyboard-focused.
		function loop(t) {
			var dt = last ? t - last : 0;
			last = t;
			if (stopped || held || !onScreen) {
				looping = false;
				last = 0;
				return;
			}
			elapsed += dt;
			nodes[index].button.style.setProperty('--p', Math.min(1, elapsed / durations[index]).toFixed(4));
			if (elapsed >= durations[index]) { show((index + 1) % quotes.length); }
			window.requestAnimationFrame(loop);
		}

		function run() {
			if (!looping && !stopped && !held && onScreen) {
				looping = true;
				window.requestAnimationFrame(loop);
			}
		}

		if (onScreen) {
			begun = true;
			show(0);
			run();
		}
	});
})();
